import type { ShotInput, SportsGameSettings, DistanceBand } from '../../sports.types';
import type {
  ISportDefinition,
  SportAssets,
  SportPhysicsParams,
  StartingPosition,
  ShotEvaluation,
} from '../sport-definition.interface';

// ─── Physics constants ─────────────────────────────────────────────────────────

const PHYSICS: SportPhysicsParams = {
  ballRadius: 0.037,            // MLB baseball ≈ 7.4 cm diameter
  launchSpeedScale: 7.0,        // Batted balls travel farther than golf
  arcHeightFactor: 1.2,   // dramatic home run arc
  assistCorrectionStrength: 0.4, // Lateral accuracy less critical — distance game
  goalToleranceRadius: 0,        // Distance sport — not used for hit-testing
  maxLandingDistance: 7.0,
  optimalElevationDeg: 35,
};

// ─── Default distance bands ────────────────────────────────────────────────────

const DEFAULT_DISTANCE_BANDS: DistanceBand[] = [
  { minDistance: 0,   points: 0,  label: 'Whiff!'    },
  { minDistance: 0.8, points: 5,  label: 'Single'    },
  { minDistance: 2.0, points: 10, label: 'Double'    },
  { minDistance: 3.5, points: 20, label: 'Home Run!' },
];

// ─── Assets ────────────────────────────────────────────────────────────────────

const ASSETS: SportAssets = {
  // Phase 3+: ballModelPath: 'assets/sports/baseball/baseball.glb'
  ballColor: '#f5f5f5', // off-white with red seam handled by future model
};

// ─── Sport definition ──────────────────────────────────────────────────────────

/**
 * Baseball / Home Run Derby
 *
 * Mechanically identical to golf-driving (distance-bands scoring, single tee).
 * Different physics constants, ball assets, ground texture, and band labels.
 * Proves the skin-swap pattern — renderer/scene zero changes needed.
 */
export class BaseballSportDefinition implements ISportDefinition {
  readonly id = 'baseball' as const;
  readonly displayName = 'Home Run Derby';
  readonly scoringModel = 'distance-bands' as const;
  readonly assets = ASSETS;
  readonly physicsParams = PHYSICS;
  readonly startingPositions: StartingPosition[] = [
    { lateralOffset: 0, distance: 0 }, // fixed batter's box
  ];
  readonly defaultDistanceBands = DEFAULT_DISTANCE_BANDS;
  readonly maxAttempts = 10;

  readonly arCameraHint = { pitch: -25, yaw: 0 };
  readonly arSceneRotation: [number, number, number] = [-Math.PI / 2, 0, 0];

  evaluateShot(
    input: ShotInput,
    assist: number,
    settings: SportsGameSettings
  ): ShotEvaluation {
    const {
      launchSpeedScale,
      optimalElevationDeg = 35,
    } = this.physicsParams;

    // ── Step 1: Whiff check ──────────────────────────────────────────────────
    if (input.overSwing) {
      return {
        scored: false,
        pointsEarned: 0,
        distance: 0,
        finalPosition: { x: 0, y: 0, z: 0 },
        isWhiff: true,
      };
    }

    // ── Step 2: Launch angle + distance ─────────────────────────────────────
    const launchAngleDeg = input.direction.y * optimalElevationDeg * 2;
    const launchAngleRad = (launchAngleDeg * Math.PI) / 180;
    const cosAngle = Math.cos(launchAngleRad);

    const baseDistance = input.power * launchSpeedScale * cosAngle;
    const noiseMagnitude = baseDistance * 0.1 * (1 - assist);
    const noise = (Math.random() * 2 - 1) * noiseMagnitude;
    const landingDistance = Math.max(0, baseDistance + noise);

    // ── Step 3: Distance band lookup ─────────────────────────────────────────
    const bands = settings.distanceBands?.length
      ? settings.distanceBands
      : this.defaultDistanceBands!;
    const band = this.resolveBand(landingDistance, bands);

    const lateralNoise = (Math.random() * 2 - 1) * 0.05 * (1 - assist);
    const finalX = input.direction.x * 0.4 * (1 - assist) + lateralNoise;

    return {
      scored: band.points > 0,
      pointsEarned: band.points,
      distance: landingDistance,
      zone: band.label,
      finalPosition: { x: finalX, y: 0, z: -landingDistance },
      isWhiff: false,
    };
  }

  private resolveBand(distance: number, bands: DistanceBand[]): DistanceBand {
    const sorted = [...bands].sort((a, b) => b.minDistance - a.minDistance);
    for (const band of sorted) {
      if (distance >= band.minDistance) return band;
    }
    return bands.reduce((min, b) => (b.minDistance < min.minDistance ? b : min));
  }
}
